/**
 * ReportHistory — list of generated/uploaded reports for an evaluation.
 *
 * Extracted from ReportWorkspace per C4 / MYD-197 so the same list can be
 * reused on the global /reports page (C5). Adds the per-row Preview action
 * (calls back into the parent so it can wire ReportPreview without the
 * history caring about modal state).
 *
 * Linear: MYD-197 (C4).
 */

import { useMemo, useState } from 'react'
import {
	FileText,
	FileSpreadsheet,
	FileType,
	FileCode,
	Trash2,
	Eye,
	ExternalLink,
	AlertCircle,
	CheckCircle2,
	Loader2,
} from 'lucide-react'

import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { cn } from 'lib/utils'

import { useDispatch } from 'react-redux'
import { fetchReportBlobThunk } from 'slices/reportSlice'
import { toast } from 'components/ui/toast-store'
import type { ReportRow, ReportScope } from 'services/reports/reportService'

const FORMAT_COLORS: Record<string, string> = {
	PDF: 'bg-red-100 text-red-700',
	DOCX: 'bg-blue-100 text-blue-700',
	HTML: 'bg-amber-100 text-amber-700',
}

// Status pill colours per C4 acceptance criterion.
const STATUS_BADGE: Record<string, string> = {
	PENDING: 'bg-gray-100 text-gray-700',
	READY: 'bg-emerald-100 text-emerald-700',
	FAILED: 'bg-red-100 text-red-700',
}

type StatusFilter = 'ALL' | 'READY' | 'FAILED'

function formatIcon(format: string) {
	switch ((format || '').toUpperCase()) {
		case 'HTML':
			return FileCode
		case 'DOCX':
			return FileType
		case 'XLSX':
		case 'CSV':
			return FileSpreadsheet
		default:
			return FileText
	}
}

function formatDate(iso?: string): string {
	if (!iso) return '—'
	try {
		return new Date(iso).toLocaleString()
	} catch {
		return iso
	}
}

interface ReportHistoryProps {
	scope: ReportScope
	entityId: string
	reports: ReportRow[]
	lastGeneratedId?: string | null
	onPreview: (report: ReportRow) => void
	onDelete: (reportId: string) => void
	emptyHint?: string
}

const ReportHistory = ({
	scope,
	entityId,
	reports,
	lastGeneratedId,
	onPreview,
	onDelete,
	emptyHint = 'No reports yet. Generate one above.',
}: ReportHistoryProps) => {
	const [filter, setFilter] = useState<StatusFilter>('ALL')
	const dispatch = useDispatch()
	// MYD-344: avoid iframe / new-tab navigation to /api/.../download (CRA's
	// proxy ignores text/html requests). Dispatch the slice thunk so the
	// fetch uses the same auth-token path as the rest of the report calls.
	const handleOpen = async (report: ReportRow) => {
		// MYD-344: always go through the backend download endpoint via the
		// blob thunk. The backend re-signs Cloudinary URLs server-side (so
		// they pass through accounts with "Restricted media types: raw" set).
		// Using absolute fileUrl directly was tried in v5 but Cloudinary
		// returned 401 for unsigned raw URLs.
		try {
			// @ts-ignore — thunks
			const result: any = await dispatch(
				fetchReportBlobThunk({ scope, entityId, reportId: report._id }),
			)
			if (result?.error) {
				toast.error(result.payload || 'Failed to open the report file.')
				return
			}
			const blob: Blob = result.payload
			const blobUrl = URL.createObjectURL(blob)
			const win = window.open(blobUrl, '_blank', 'noopener,noreferrer')
			// Best-effort cleanup — browsers vary on how aggressively they
			// release blob URLs after window.open. 60s is plenty for the
			// new tab to fetch the bytes.
			setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
			if (!win) {
				// Popup blocked — fall back to a direct download via a hidden
				// anchor with the download attribute.
				const a = document.createElement('a')
				a.href = blobUrl
				a.download = ''
				document.body.appendChild(a)
				a.click()
				a.remove()
			}
		} catch {
			toast.error('Failed to open the report file.')
		}
	}

	// Server already returns reports sorted by generatedAt desc (compound
	// index added in C2). We sort client-side too — defensive, since
	// generateReport.fulfilled prepends optimistically and the prepended
	// row may not match server order until the next refetch.
	const visible = useMemo(() => {
		const sorted = [...reports].sort((a, b) => {
			const aTime = new Date(a.generatedAt).getTime() || 0
			const bTime = new Date(b.generatedAt).getTime() || 0
			return bTime - aTime
		})
		if (filter === 'ALL') return sorted
		return sorted.filter((r) => r.status === filter)
	}, [reports, filter])

	const counts = useMemo(() => {
		const result = { all: reports.length, ready: 0, failed: 0 }
		for (const r of reports) {
			if (r.status === 'READY') result.ready += 1
			if (r.status === 'FAILED') result.failed += 1
		}
		return result
	}, [reports])

	return (
		<Card>
			<CardHeader className="pb-3 flex-row items-center justify-between gap-3 flex-wrap">
				<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
					<FileText className="w-4 h-4" />
					History ({counts.all})
				</h4>
				{counts.all > 0 && (
					<div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
						{(
							[
								{ key: 'ALL', label: `All (${counts.all})` },
								{ key: 'READY', label: `Ready (${counts.ready})` },
								{ key: 'FAILED', label: `Failed (${counts.failed})` },
							] as const
						).map((opt) => {
							const active = filter === opt.key
							return (
								<button
									key={opt.key}
									type="button"
									onClick={() => setFilter(opt.key)}
									className={cn(
										'px-2.5 py-1 text-xs font-medium rounded transition-colors',
										active
											? 'bg-[#0080fe] text-white'
											: 'text-gray-600 hover:bg-gray-100',
									)}
								>
									{opt.label}
								</button>
							)
						})}
					</div>
				)}
			</CardHeader>
			<CardContent>
				{visible.length === 0 ? (
					<div className="text-center py-10">
						<FileText className="mx-auto h-10 w-10 text-gray-300 mb-2" />
						<p className="text-sm text-gray-500 max-w-md mx-auto">
							{counts.all === 0
								? emptyHint
								: 'No reports match this filter.'}
						</p>
					</div>
				) : (
					<div className="space-y-2">
						{visible.map((r) => {
							const Icon = formatIcon(r.format)
							const generator =
								(typeof r.generatedBy === 'object' ? r.generatedBy : null) ?? null
							const generatorName =
								generator?.name || generator?.email || 'User'
							const isFailed = r.status === 'FAILED'
							const isPending = r.status === 'PENDING'
							const isReady = r.status === 'READY'
							const isJustGenerated = r._id === lastGeneratedId
							return (
								<div
									key={r._id}
									className={cn(
										'flex items-center gap-3 px-3 py-2.5 rounded-md border',
										isFailed
											? 'border-red-200 bg-red-50/40'
											: isJustGenerated
												? 'border-[#0080fe]/30 bg-[#0080fe]/5'
												: 'border-gray-100 hover:border-gray-200',
									)}
								>
									<div
										className={cn(
											'shrink-0 w-10 h-10 rounded-md flex items-center justify-center',
											isFailed ? 'bg-red-100' : 'bg-gray-50',
										)}
									>
										<Icon
											className={cn(
												'w-5 h-5',
												isFailed ? 'text-red-500' : 'text-gray-500',
											)}
										/>
									</div>
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2 flex-wrap">
											<span className="font-medium text-sm text-gray-900 truncate">
												{r.templateName || r.templateKey || 'Untitled report'}
											</span>
											<Badge
												className={cn(
													'text-[10px]',
													FORMAT_COLORS[(r.format || '').toUpperCase()] ||
														'bg-gray-100 text-gray-600',
												)}
											>
												{r.format || '—'}
											</Badge>
											<Badge
												className={cn(
													'text-[10px] gap-1',
													STATUS_BADGE[r.status] || 'bg-gray-100 text-gray-700',
												)}
											>
												{isPending && <Loader2 className="w-3 h-3 animate-spin" />}
												{isFailed && <AlertCircle className="w-3 h-3" />}
												{isReady && isJustGenerated && (
													<CheckCircle2 className="w-3 h-3" />
												)}
												{r.status}
											</Badge>
										</div>
										<div className="text-xs text-gray-500 mt-0.5 truncate">
											Generated by{' '}
											<span className="text-gray-700">{generatorName}</span> ·{' '}
											{formatDate(r.generatedAt)}
											{isFailed && r.error && (
												<span className="text-red-600"> · {r.error}</span>
											)}
										</div>
									</div>
									<div className="flex items-center gap-1 shrink-0">
										{isReady && (
											<>
												<button
													type="button"
													onClick={() => onPreview(r)}
													title="Preview"
													aria-label="Preview"
													className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-600 border border-gray-200 hover:bg-gray-50"
												>
													<Eye className="w-3.5 h-3.5" />
													Preview
												</button>
												<button
													type="button"
													onClick={() => handleOpen(r)}
													title="Download"
													aria-label="Download"
													className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-[#0080fe] border border-[#0080fe]/20 hover:bg-[#0080fe]/5"
												>
													<ExternalLink className="w-3.5 h-3.5" />
													Open
												</button>
											</>
										)}
										{/* Only for a report this user may delete: its generator or
										    the asset's owner (MYD-524). Absent means no rule was
										    sent (evaluation reports), and Delete shows as before. */}
										{r.canDelete !== false && (
											<button
												type="button"
												onClick={() => onDelete(r._id)}
												title="Delete report"
												aria-label="Delete report"
												className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-gray-50"
											>
												<Trash2 className="w-3.5 h-3.5" />
											</button>
										)}
									</div>
								</div>
							)
						})}
					</div>
				)}
			</CardContent>
		</Card>
	)
}

export default ReportHistory
